"use client";

import { useEffect, useState, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SECTIONS = [
  {
    trigger: ".section-inference",
    title: "Inference Layer",
    body:
      "Direct execution plane for intelligence workloads. Compute, memory, and model state converge into a unified runtime.",
  },
  {
    trigger: ".section-distributed",
    title: "Distributed Execution",
    body:
      "Independent compute resources aggregate into a coherent execution domain that expands and contracts dynamically.",
  },
  {
    trigger: ".section-memory",
    title: "Memory Fabric",
    body:
      "High-velocity state propagation across execution nodes, enabling continuous intelligence at scale.",
  },
  {
    trigger: ".section-wireframe",
    title: "Silicon Skeleton",
    body:
      "Architecture expressed as execution pathways - raw compute reduced to its structural essence.",
  },
];

type Section = (typeof SECTIONS)[number];

export default function MobileSectionCaption() {
  const [content, setContent] = useState<Section | null>(null);
  const captionRef = useRef<HTMLDivElement>(null);

  /* --------------------------------------------------
     1️⃣ ScrollTrigger logic (section enter / leave)
  -------------------------------------------------- */
  useEffect(() => {
    if (window.innerWidth >= 768) return;

    SECTIONS.forEach((section, index) => {
      const isLast = index === SECTIONS.length - 1;

      ScrollTrigger.create({
        trigger: section.trigger,
        start: "top 60%",
        end: "bottom 60%",
        onEnter: () => setContent(section),
        onEnterBack: () => setContent(section),
        onLeave: () => {
          if (isLast) setContent(null);
        },
        onLeaveBack: () => {
          if (index === 0) setContent(null);
        },
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  /* --------------------------------------------------
     2️⃣ Fade / slide animation when content changes
  -------------------------------------------------- */
  useEffect(() => {
    if (!captionRef.current) return;

    gsap.to(captionRef.current, {
      opacity: content ? 1 : 0,
      y: content ? 0 : 20,
      duration: 0.3,
      ease: "power2.out",
    });
  }, [content]);

  /* --------------------------------------------------
     3️⃣ Guard render (TypeScript + logic safety)
  -------------------------------------------------- */
  if (!content) return null;

  return (
    <div
      ref={captionRef}
      className="fixed bottom-0 left-0 w-full z-40 md:hidden pointer-events-none opacity-0"
    >
      <div className="mx-4 mb-4 rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10 p-5">
        <h3 className="text-xl font-black uppercase tracking-tight text-white">
          {content.title}
        </h3>
        <p className="mt-2 text-xs font-mono text-blue-300 leading-relaxed opacity-80">
          {content.body}
        </p>
      </div>
    </div>
  );
}
